import { useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { translateError } from "@/i18n/errors";
import { sendChunks, startUpload } from "@/lib/chunkedUpload";
import { FilePick } from "@/components/admin/FilePick";
import { IconBadge } from "@/components/admin/IconBadge";
import { PlatformFields } from "@/components/admin/PlatformFields";

// Creating an app can be just: pick an APK → Enregistrer (name, icon and version are read from the manifest).
export const AppFormDialog = ({ open, onOpenChange, app, onSaved }) => {
  const { t } = useI18n();
  const { base, isDev } = useSpace();
  const declared = app?.platform?.category_declared;
  const [category, setCategory] = useState(declared ? app.category : "");
  const [compat, setCompat] = useState(app?.platform?.declared || "");
  const initialUrl = app?.platform?.iphone_url || "";
  const [url, setUrl] = useState(initialUrl);
  const [name, setName] = useState(app?.name || "");
  const [description, setDescription] = useState(app?.description || "");
  const [icon, setIcon] = useState(null);
  const [apk, setApk] = useState(null);
  const [version, setVersion] = useState("");
  const [editVersion, setEditVersion] = useState(false);
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);

  const pickApk = ([f]) => {
    if (f && !f.name.toLowerCase().endsWith(".apk")) return toast.error(t("err.apkOnly"));
    setApk(f || null);
  };

  // Auto upload (no app id): the server matches the package in this owner's catalogue or creates the app.
  const uploadApk = async () => {
    setProgress(0);
    const init = await startUpload({ base, appId: null, file: apk, version: editVersion ? version.trim() : "" });
    return sendChunks({ base, uploadId: init.upload_id, file: apk, chunkSize: init.chunk_size, totalChunks: init.total_chunks, onProgress: setProgress });
  };

  const saveFields = async (target, fallbackName = "", keepDesc = false) => {
    const form = new FormData();
    form.append("name", name.trim() || fallbackName);
    if (!target && !name.trim()) form.append("auto_name", "true");
    form.append("description", keepDesc ? description.trim() || target.description || "" : description);
    if (icon) form.append("icon", icon);
    const { data } = target ? await api.put(`${base}/apps/${target.id}`, form) : await api.post(`${base}/apps`, form);
    return data;
  };

  // Developers: always (required fields). Official: only when the admin changed the iPhone link.
  const savePlatform = async (target) => {
    if (!isDev && url.trim() === initialUrl) return target;
    const body = isDev ? { category, compat, iphone_url: url.trim() } : { iphone_url: url.trim() };
    const { data } = await api.put(`${base}/apps/${target.id}/platform`, body);
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let data;
      if (!app && apk) {
        const res = await uploadApk();
        data = res.app;
        if (name.trim() || description.trim() || icon) data = await saveFields(res.app, res.app.name, true);
        data = await savePlatform(data);
        toast.success((res.result === "new_app" ? t("bulk.newApp", { name: data.name }) : t("bulk.newVersion", { name: data.name, v: res.version.label })) + (res.dedup ? ` · ${t("upload.dedup")}` : ""));
      } else {
        data = await savePlatform(await saveFields(app));
        toast.success(t(app ? "form.updated" : "form.created"));
      }
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const resetIcon = async () => {
    try {
      const { data } = await api.post(`${base}/apps/${app.id}/icon/reset`);
      toast.success(t("icon.resetDone"));
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="card-carbon max-h-[90vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto rounded-2xl" data-testid="app-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider gold-text">{t(app ? "form.editTitle" : "form.newTitle")}</DialogTitle>
          <DialogDescription>{t(app ? "form.desc" : "form.descAuto")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="min-w-0 space-y-4">
          {!app && (
            <div className="min-w-0 space-y-2 rounded-xl border border-[rgba(245,197,66,0.25)] bg-black/30 p-3" data-testid="app-form-apk-section">
              <Label htmlFor="app-apk">{t("form.apk")}</Label>
              <FilePick id="app-apk" accept=".apk,application/vnd.android.package-archive" files={apk ? [apk] : null} onFiles={pickApk} disabled={saving} testId="app-form-apk-input" />
              <p className="text-xs text-neutral-500">{t("form.apkHint")}</p>
              {apk && (editVersion ? (
                <Input value={version} maxLength={40} placeholder={t("form.versionOverride")} onChange={(e) => setVersion(e.target.value)} data-testid="app-form-version-input" />
              ) : (
                <p className="text-xs text-neutral-400">{t("form.versionAuto")}{" "}
                  <button type="button" className="text-[var(--gold)] underline" onClick={() => setEditVersion(true)} data-testid="app-form-version-edit-btn">{t("form.editVersion")}</button>
                </p>
              ))}
              {progress !== null && (
                <div className="h-2 overflow-hidden rounded-full bg-black/60" data-testid="app-form-apk-progress"><div className="progress-fill h-full rounded-full" style={{ width: `${progress}%` }} /></div>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="app-name">{t(!app && apk ? "form.nameAuto" : "form.name")}</Label>
            <Input id="app-name" required={app || !apk} value={name} onChange={(e) => setName(e.target.value)} data-testid="app-form-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="app-desc">{t("form.description")}</Label>
            <Textarea id="app-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="app-form-description-input" />
          </div>
          <div className="min-w-0 space-y-2">
            <Label htmlFor="app-icon">{t("form.icon")}</Label>
            {app?.icon_url && <IconBadge app={app} />}
            <FilePick id="app-icon" accept="image/png,image/jpeg,image/webp,image/svg+xml" files={icon ? [icon] : null} onFiles={([f]) => setIcon(f || null)}
              disabled={saving} testId="app-form-icon-input" label={app?.icon_url ? t("icon.change") : undefined} />
            {app?.icon_source === "manual" && app?.active_version && (
              <button type="button" className="inline-flex items-center gap-1.5 text-xs text-[var(--gold)] underline" onClick={resetIcon} data-testid="app-form-icon-reset-btn">
                <RotateCcw className="h-3.5 w-3.5" /> {t("icon.reset")}
              </button>
            )}
          </div>
          <PlatformFields isDev={isDev} app={app} v={{ category, setCategory, compat, setCompat, url, setUrl }} />
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button type="button" className="btn-red btn-sm" disabled={saving} onClick={() => onOpenChange(false)} data-testid="app-form-cancel-btn">{t("form.cancel")}</button>
            <button type="submit" className="btn-gold btn-sm" disabled={saving} data-testid="app-form-submit-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("form.save")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
